import { BCMSCurrency, Link } from '@bcm-dev/bcms-vite';
import { Bill } from "model";
import React from "react";

import WidgetContainer from "compid/shared/containers/WidgetContainer";
import WidgetHeaderView from "compid/shared/containers/WidgetContainer/views/WidgetHeaderView";
import TableView, { TableRowView } from "compid/shared/views/TableView";
import WidgetView, { WidgetMainContentView } from "compid/shared/views/WidgetView";
import UIButton from "compid/ui/UIButton";

import { SVGAsset } from "res";
import './styles.css';

/**
 * @function MultipayWidget
 * @param {ARRAY} bills - The bills that are shown in the multipay
 * @param {ARRAY} items - The items that are to be shown in the table
 * @param {FUNCTION} onSubmit - The function to be ran when the submit button is clicked
 * @param {FUNCTION} onCancel - The function to be ran when the cancel button is clicked
 * @returns {React.Component}
 * @exported true
 * @description The main multipay component
 */
export default function MultipayWidget(props) {
  const bills = props.bills ?? [];
  const items = props.items ?? [];
  const onSubmit = props.onSubmit ?? undefined;
  const onCancel = props.onCancel ?? undefined;
  const showTotals = props.showTotal ?? true;

  const hasSelection = bills.length > 0;

  const totalAmount = bills.reduce((result, bill) => {
    result += parseFloat(bill.amount.amount ?? 0);
    result += parseFloat(bill.feeAmount.amount ?? 0);
    return result;
  }, 0);

  return(
    <WidgetContainer className="multipay-widget">
      <WidgetHeaderView title={
                          <div className="flex-container flex-horizontal align-center">
                            Multipay
                            <SVGAsset id="InfoIcon" className="right-icon header-icon" />
                          </div> } 
                        dataPoint="Multipay_Header"/>
      <WidgetView>
        <WidgetMainContentView>
          
          {/* When bills are selected this shows */}
          <div className={ `flex-width flex-container flex-vertical bills-selected ${ !hasSelection ? "hide" : "" }` }>
            <UIButton onClick={ onSubmit } className="primary-button" dataPoint="Multipay_Pay_Button">Pay { bills.length } selected bill{ bills.length > 1 ? "s" : "" }</UIButton>
            <UIButton className="secondary-button" onClick={ onCancel }>
              Cancel
            </UIButton>

            <div className="selected-biller-container">
              <TableView>
                { items }

                { showTotals && <TableRowView label="Total" data={ BCMSCurrency.toCurrency(totalAmount) } dataClass="text-right" rowClassName="total-row"  /> }
              </TableView>
            </div>
          </div>

          {/* When no bills are selected this shows - This is shown by default */}
          <div className={ `flex-width flex-container flex-vertical no-bills-selected ${ hasSelection ? "hide" : "" }` }>
            <div className="flex-height">
              <h4>Need to make multiple payments?</h4>
            </div>
            <div className="flex-height">
              <p className="sub-text">Click on checkboxes to select one or more billers and get started with Multipay.</p>
            </div>
          </div>

        </WidgetMainContentView>
      </WidgetView>
    </WidgetContainer>
  );
}

/**
 * @function MultiPayRemoveItem
 * @param {Bill} bill - The bill that should be used to populate the data
 * @returns {React.Component}
 * @exported true
 * @description Multipay table item that shows a remove link
 */
export function MultiPayRemoveItem(props) {
  const bill = props.bill ?? Bill.firstInstanceOf();
  const { name, nickname, number } = bill.biller;

  const onClick = props.onRemove ?? undefined;

  return (
    <TableRowView
      label={ <MultiPayItemLabel name={ name } nickname={ nickname } number={ number } /> }
      data={ <Link onClick={ onClick }>Remove</Link> }
      dataClass="text-right"
      rowClassName="multipay-biller-row"
    />
  );
}

/**
 * @function MultiPayAmountItem
 * @param {Bill} bill - The bill that should be used to populdate the data
 * @returns {React.Component}
 * @exported true
 * @description Multipay table item that shows an amount value
 */
export function MultiPayAmountItem(props) {
  const bill = props.bill ?? Bill.firstInstanceOf();
  const { name, nickname, number } = bill.biller;

  return (
    <TableRowView
      label={ <MultiPayItemLabel name={ name } nickname={ nickname } number={ number } /> }
      data={ bill.amount.format() }
      dataClass="text-right"
      rowClassName="multipay-biller-row"
    />
  );
}

/**
 * @function MultiPayItemLabel
 * @param {STRING} name - The name of the biller
 * @param {STRING} nickname - The nickname of the biller
 * @param {STRING} number - The number of the biller
 * @returns {React.Component}
 * @exported true
 * @description The label that is shown for the multipay table items
 */
export function MultiPayItemLabel(props) {
  const name = props.name ?? "[NAME]";
  const nickname = props.nickname ?? "[NICKNAME]";
  const number = props.number ?? "[NUMBER]";

  return (
    <div className="biller-wrapper">
      <h4>{ name }</h4>
      <p className="sub-text gray-text">{ nickname }{ number }</p>
    </div>
  );
}